import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { XLg } from 'react-bootstrap-icons';
import axios from 'axios';
import { API_URL, WHITELABEL_ID } from '../constants';
import { AuthContext } from './AuthContext';
import { ConfigContext } from './ConfigContext';
import './Header.css';

const Header = () => {
  const config = useContext(ConfigContext);
  const { token, setToken, admin, setAdmin, hasRole, hasPermission } =
    useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const toggle = () => setIsOpen((open) => !open);
  const closeMenu = () => setIsOpen(false);

  const handleMenuClick = (event) => {
    const link = event.target.closest('a');

    if (link && !link.classList.contains('dropdown-toggle')) {
      closeMenu();
    }
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.body.classList.add('wl-menu-open');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('wl-menu-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const onLogout = async () => {
    localStorage.removeItem('authToken');

    setIsLoggingOut(true); // Set logging out to true
    try {
      // Make an API call to logout from the backend
      await axios.delete(
        `${API_URL}/api/wl/${WHITELABEL_ID}/manager/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Clear the token from local storage (or wherever it's stored)
      localStorage.removeItem('authToken');
      // Update the token in the AuthContext
      setToken(null);
      setAdmin(null);

      // Redirect the user to the login or home page
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
      localStorage.removeItem('authToken');
      setToken(null);
      setAdmin(null);
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(' ')
          .map((part) => part[0])
          .join('')
          .toUpperCase()
      : '';
  };

  return (
    <nav
      className="navbar navbar-expand-xl navbar-light bg-light wl-navbar"
      style={{
        '--wl-menu-primary': config?.theme?.primaryColor || '#1769d2',
      }}
    >
      <Link className="navbar-brand wl-navbar-brand" to="/" onClick={closeMenu}>
        <img src={config.icons.bigIconUrl} height="50" alt="Company logo" />
      </Link>
      {token ? (
        <>
          {isOpen && (
            <button
              type="button"
              className="wl-menu-backdrop"
              onClick={closeMenu}
              aria-label="Close navigation menu"
            />
          )}
          <div
            className={`collapse navbar-collapse wl-navbar-menu ${
              isOpen ? 'show' : ''
            }`}
            id="wlNavbarMenu"
            onClick={handleMenuClick}
          >
            <div className="wl-drawer-header">
              <Link className="wl-drawer-brand" to="/" onClick={closeMenu}>
                <img src={config.icons.bigIconUrl} alt="Company logo" />
              </Link>
              <button
                type="button"
                className="wl-drawer-close"
                onClick={closeMenu}
                aria-label="Close navigation menu"
              >
                <XLg aria-hidden="true" />
              </button>
            </div>
            <ul className="navbar-nav wl-main-menu">
              <li className="nav-item">
                <Link
                  className={`nav-link ${
                    location.pathname === '/dashboard' ? 'active' : ''
                  }`}
                  to="/dashboard"
                >
                  Dashboard
                </Link>
              </li>
              {hasPermission('driver-view') && (
                <li className="nav-item">
                  <Link
                    className={`nav-link ${
                      location.pathname === '/fleet-live-tracking'
                        ? 'active'
                        : ''
                    }`}
                    to="/fleet-live-tracking"
                  >
                    Fleet Live Tracking
                  </Link>
                </li>
              )}
              {hasPermission('pickup-view') && (
                <li className="nav-item dropdown">
                  <button
                    type="button"
                    className={`nav-link dropdown-toggle ${
                      location.pathname.startsWith('/pickups') ||
                      location.pathname.startsWith('/schedule-pickups') ||
                      location.pathname.startsWith('/slot-pickups')
                        ? 'active'
                        : ''
                    }`}
                    id="deliveriesDropdown"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Deliveries
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="deliveriesDropdown"
                  >
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/pickups/live-requests"
                      >
                        Live Requests
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/schedule-pickups">
                        Schedule Deliveries
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/slot-pickups">
                        Slot Deliveries
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/pickups">
                        Delivery List
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/pickups/statistics/by_day"
                      >
                        Statistics by Day
                      </Link>
                    </li>
                    {hasPermission('sales-view') && (
                      <li>
                        <Link className="dropdown-item" to="/pickups/sales">
                          Sales
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}
              {hasPermission('driver-view') && (
                <li className="nav-item dropdown">
                  <button
                    type="button"
                    className={`nav-link dropdown-toggle ${
                      location.pathname.startsWith('/drivers') ||
                      location.pathname.startsWith('/vehicles')
                        ? 'active'
                        : ''
                    }`}
                    id="driversDropdown"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Drivers
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="driversDropdown"
                  >
                    <li>
                      <Link className="dropdown-item" to="/drivers">
                        Driver List
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/drivers/create">
                        Create Driver
                      </Link>
                    </li>
                    {hasPermission('vehicle-view') && (
                      <>
                        <li>
                          <Link className="dropdown-item" to="/vehicles">
                            Vehicles
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to="/vehicles/create">
                            Create Vehicle
                          </Link>
                        </li>
                      </>
                    )}
                    <li>
                      <Link className="dropdown-item" to="/drivers/performance">
                        Performance
                      </Link>
                    </li>
                    {hasPermission('driver-update') && (
                      <>
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/drivers/notify-all"
                          >
                            Notify All
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>
                </li>
              )}
              {hasPermission('user-view') && (
                <li className="nav-item dropdown">
                  <button
                    type="button"
                    className={`nav-link dropdown-toggle ${
                      location.pathname.startsWith('/users') ? 'active' : ''
                    }`}
                    id="customersDropdown"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Customers
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="customersDropdown"
                  >
                    <li>
                      <Link className="dropdown-item" to="/users">
                        Customer List
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/users/create">
                        Create Customer
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/users/performance">
                        Performance
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/users/statistics/signups/by_week"
                      >
                        Signups by Week
                      </Link>
                    </li>
                    {hasPermission('user-update') && (
                      <>
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/users/notify-all"
                          >
                            Notify All
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>
                </li>
              )}
              {hasPermission('invoice-view') && (
                <li className="nav-item">
                  <Link
                    className={`nav-link ${
                      location.pathname.startsWith('/invoices') ? 'active' : ''
                    }`}
                    to="/invoices"
                  >
                    Invoices
                  </Link>
                </li>
              )}
              {/*{hasPermission("cargo-insurance-view") && (*/}
              {/*    <li className="nav-item">*/}
              {/*        <Link className={`nav-link ${location.pathname.startsWith("/cargo-insurances") ? "active" : ""}`} to="/cargo-insurances">*/}
              {/*            Insurances*/}
              {/*        </Link>*/}
              {/*    </li>*/}
              {/*)}*/}
              {hasPermission('truck-type-view') && (
                <li className="nav-item">
                  <Link
                    className={`nav-link ${
                      location.pathname.startsWith('/truck-types/prices')
                        ? 'active'
                        : ''
                    }`}
                    to="/truck-types/prices"
                  >
                    Prices
                  </Link>
                </li>
              )}
              {hasPermission('tax-rate-view') && (
                <li className="nav-item">
                  <Link
                    className={`nav-link ${location.pathname.startsWith('/tax-rates') ? 'active' : ''}`}
                    to="/tax-rates"
                  >
                    Taxes
                  </Link>
                </li>
              )}
              {hasRole('super-manager') && (
                <li className="nav-item dropdown">
                  <button
                    type="button"
                    className={`nav-link dropdown-toggle ${
                      location.pathname.startsWith('/managers') ? 'active' : ''
                    }`}
                    id="managersDropdown"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Managers
                  </button>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="managersDropdown"
                  >
                    <li>
                      <Link className="dropdown-item" to="/managers">
                        Managers
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/managers/create">
                        Create Manager
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/managers/roles">
                        Roles
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/managers/roles/create"
                      >
                        Create Role
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/managers/permissions"
                      >
                        Permissions
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/managers/permissions/create"
                      >
                        Create Permission
                      </Link>
                    </li>
                  </ul>
                </li>
              )}
            </ul>
            <ul className="navbar-nav ml-auto wl-profile-menu">
              <li className="nav-item dropdown">
                <button
                  type="button"
                  className="nav-link dropdown-toggle d-flex align-items-center wl-profile-toggle"
                  id="adminDropdown"
                  data-toggle="dropdown"
                  aria-expanded="false"
                >
                  {admin?.avatar ? (
                    <span
                      className="wl-profile-avatar"
                      style={{
                        backgroundImage: `url(${admin.avatar})`,
                      }}
                    />
                  ) : (
                    <span className="default-avatar">
                      {getInitials(admin?.name)}
                    </span>
                  )}
                  <span className="wl-profile-name">{admin?.name}</span>
                </button>
                <ul
                  className="dropdown-menu dropdown-menu-right"
                  aria-labelledby="adminDropdown"
                >
                  <li>
                    <Link className="dropdown-item" to="/my-profile">
                      My Profile
                    </Link>
                  </li>
                  {hasRole('super-manager') && (
                    <li>
                      <Link className="dropdown-item" to="/manage-billing">
                        Manage Billing
                      </Link>
                    </li>
                  )}
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={onLogout}>
                      {isLoggingOut ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                          &nbsp;Logging out...
                        </>
                      ) : (
                        'Logout'
                      )}
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <button
            className="navbar-toggler wl-navbar-toggler"
            type="button"
            onClick={toggle}
            aria-controls="wlNavbarMenu"
            aria-expanded={isOpen}
            aria-label={
              isOpen ? 'Close navigation menu' : 'Open navigation menu'
            }
          >
            <span className="wl-menu-icon" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </>
      ) : null}
    </nav>
  );
};

export default Header;
